import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAccounts } from '../context/AccountContext';
import { useAuth } from '../context/AuthContext';

// Navbar dropdown for choosing which connected MT5 account the dashboard is
// showing. "All accounts" is the portfolio view.
//
// Hidden entirely when the user has one account or none: a switcher with a
// single entry is noise, and the dashboards behave identically either way.

const DOT = {
  running: { color: 'var(--success, #2ECC71)', title: 'Connected' },
  starting: { color: 'var(--warning, #F39C12)', title: 'Starting' },
  awaiting_manual_login: { color: 'var(--warning, #F39C12)', title: 'Awaiting login' },
  needs_attention: { color: 'var(--warning, #F39C12)', title: 'Needs attention' },
  manual_login_timeout: { color: 'var(--danger, #E74C3C)', title: 'Login timed out' },
  auth_failed: { color: 'var(--danger, #E74C3C)', title: 'Login failed' },
  error: { color: 'var(--danger, #E74C3C)', title: 'Error' },
  stopped: { color: 'var(--text-tertiary, #888)', title: 'Stopped' },
  none: { color: 'var(--text-tertiary, #888)', title: 'Not running' },
};

function StatusDot({ account }) {
  const state = account.status === 'disconnected'
    ? { color: 'var(--text-tertiary, #888)', title: 'Disconnected' }
    : account.status === 'pending'
      ? { color: 'var(--warning, #F39C12)', title: 'Awaiting verification' }
      : (DOT[account.containerStatus] || DOT.none);
  return (
    <span
      title={state.title}
      aria-label={state.title}
      style={{
        display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
        background: state.color, marginRight: 8, flex: '0 0 auto',
      }}
    />
  );
}

export default function AccountSwitcher() {
  const { accounts, selectedId, selected, select } = useAccounts();
  const { user } = useAuth();
  // The Accounts page lives inside each role's layout (/trader/accounts,
  // /copier/accounts), so the link is role-relative.
  const accountsPath = `/${user?.role || 'copier'}/accounts`;
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  if (accounts.length < 2) return null;

  const label = selected ? (selected.displayLabel || selected.label) : 'All accounts';

  return (
    <div className="account-switcher" ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary"
        onClick={() => setOpen(v => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{ display: 'inline-flex', alignItems: 'center', maxWidth: 220 }}
      >
        {selected && <StatusDot account={selected} />}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </span>
        <span aria-hidden="true" style={{ marginLeft: 6, opacity: 0.7 }}>▾</span>
      </button>

      {open && (
        <div
          role="listbox"
          className="card"
          style={{
            position: 'absolute', right: 0, top: '100%', marginTop: 6, zIndex: 1050,
            minWidth: 260, padding: 6, boxShadow: '0 10px 30px rgba(0,0,0,.45)',
          }}
        >
          <button
            type="button"
            role="option"
            aria-selected={!selectedId}
            className="btn btn-sm btn-link text-left"
            style={{ display: 'block', width: '100%', textAlign: 'left' }}
            onClick={() => { select(null); setOpen(false); }}
          >
            <strong>All accounts</strong>
            <div className="text-muted small">Combined across {accounts.length} accounts</div>
          </button>

          <div style={{ borderTop: '1px solid rgba(255,255,255,.08)', margin: '4px 0' }} />

          {accounts.map((a) => (
            <button
              key={a.id}
              type="button"
              role="option"
              aria-selected={a.id === selectedId}
              className="btn btn-sm btn-link text-left"
              style={{
                display: 'flex', alignItems: 'flex-start', width: '100%', textAlign: 'left',
                fontWeight: a.id === selectedId ? 600 : 400,
              }}
              onClick={() => { select(a.id); setOpen(false); }}
            >
              <StatusDot account={a} />
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {a.displayLabel || a.label}
                </span>
                <span className="text-muted small" style={{ display: 'block' }}>
                  {a.server} · #{a.accountNumber}
                  {a.status !== 'verified' ? ` · ${a.status}` : ''}
                </span>
              </span>
            </button>
          ))}

          <div style={{ borderTop: '1px solid rgba(255,255,255,.08)', margin: '4px 0' }} />
          <Link
            to={accountsPath}
            className="btn btn-sm btn-link text-left"
            style={{ display: 'block', width: '100%', textAlign: 'left' }}
            onClick={() => setOpen(false)}
          >
            Manage MT5 accounts →
          </Link>
        </div>
      )}
    </div>
  );
}
